import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Download } from "lucide-react";
import { downloadCsv } from "@/pages/admin/AdminCustomers";

// Order history: every finished order with how long each stage took —
// received → preparing → ready → served → paid — so the kitchen's speed is visible.
const PRESETS = [
  { key: "today", label: "Today" },
  { key: "yesterday", label: "Yesterday" },
  { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" },
  { key: "all", label: "All" },
  { key: "custom", label: "Custom" },
];

const dayStart = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
const plusDays = (d, n) => new Date(d.getTime() + n * 86400000);
const ymd = (d) => [d.getFullYear(), String(d.getMonth() + 1).padStart(2, "0"), String(d.getDate()).padStart(2, "0")].join("-");

// [from, to) as local-day boundaries (the staff's browser runs on restaurant
// time); null = unbounded. `to` is exclusive, so a day means the whole day.
function rangeBounds(preset, fromDate, toDate) {
  const today = dayStart(new Date());
  if (preset === "today") return [today, plusDays(today, 1)];
  if (preset === "yesterday") return [plusDays(today, -1), today];
  if (preset === "7d") return [plusDays(today, -6), plusDays(today, 1)];
  if (preset === "30d") return [plusDays(today, -29), plusDays(today, 1)];
  if (preset === "custom") {
    return [fromDate ? dayStart(new Date(fromDate + "T00:00:00")) : null,
            toDate ? plusDays(dayStart(new Date(toDate + "T00:00:00")), 1) : null];
  }
  return [null, null];
}

export default function AdminHistory() {
  const [orders, setOrders] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [preset, setPreset] = useState("7d");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const [from, to] = useMemo(() => rangeBounds(preset, fromDate, toDate), [preset, fromDate, toDate]);
  const params = useMemo(() => {
    const p = { status: "completed,cancelled" };
    if (from) p.from = from.toISOString();
    if (to) p.to = to.toISOString();
    return p;
  }, [from, to]);
  useEffect(() => { api.get("/admin/orders", { params }).then((r) => setOrders(r.data)); }, [params]);

  const shown = orders.filter((o) => {
    if (status !== "all" && o.status !== status) return false;
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return o.id.toLowerCase().includes(s) || (o.table_label || "").toLowerCase().includes(s);
  });
  const revenue = shown.filter((o) => o.status === "completed").reduce((sum, o) => sum + (Number(o.total) || 0), 0);

  // The export follows what is on screen: same date range, same status filter.
  const exportCsv = () => {
    const p = new URLSearchParams({ status: status === "all" ? "completed,cancelled" : status });
    if (from) p.set("from", from.toISOString());
    if (to) p.set("to", to.toISOString());
    const label = from || to ? `${from ? ymd(from) : "start"}_to_${to ? ymd(plusDays(to, -1)) : "today"}` : "all";
    downloadCsv(`/admin/export/orders.csv?${p.toString()}`, `orders_${label}.csv`);
  };

  return (
    <div>
      <header className="mb-6 flex flex-wrap items-end justify-between gap-3">
        <div>
          <div className="text-xs uppercase tracking-widest text-gray-400">Past orders</div>
          <h1 className="font-display text-2xl font-bold tracking-tight mt-1">History</h1>
        </div>
        <button data-testid="orders-export" onClick={exportCsv}
          className="inline-flex items-center gap-2 text-sm font-semibold px-4 h-10 rounded-lg border border-gray-200 bg-white hover:bg-gray-50">
          <Download className="w-4 h-4" aria-hidden /> Export CSV (items + GST)
        </button>
      </header>

      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden text-sm">
          {PRESETS.map((r) => (
            <button key={r.key} data-testid={`hist-range-${r.key}`} onClick={() => setPreset(r.key)}
              className={`px-3 h-9 ${preset === r.key ? "bg-[var(--qd-charcoal)] text-white" : "hover:bg-gray-50"}`}>{r.label}</button>
          ))}
        </div>
        {preset === "custom" && (
          <>
            <input type="date" data-testid="hist-from" value={fromDate} max={toDate || undefined} onChange={(e) => setFromDate(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-200 bg-white text-sm" />
            <span className="text-xs text-gray-400">to</span>
            <input type="date" data-testid="hist-to" value={toDate} min={fromDate || undefined} onChange={(e) => setToDate(e.target.value)}
              className="h-9 px-2 rounded-lg border border-gray-200 bg-white text-sm" />
          </>
        )}
      </div>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="inline-flex rounded-lg border border-gray-200 bg-white overflow-hidden text-sm">
          {["all", "completed", "cancelled"].map((s) => (
            <button key={s} data-testid={`hist-filter-${s}`} onClick={() => setStatus(s)}
              className={`px-3 h-9 capitalize ${status === s ? "bg-[var(--qd-charcoal)] text-white" : "hover:bg-gray-50"}`}>{s}</button>
          ))}
        </div>
        <input data-testid="hist-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order # or table…"
          className="h-9 px-3 rounded-lg border border-gray-200 bg-white text-sm w-56" />
        <span className="text-xs text-gray-400 ml-auto">
          {shown.length} order{shown.length === 1 ? "" : "s"} · ₹{revenue.toLocaleString("en-IN", { maximumFractionDigits: 2 })}
          {orders.length >= 500 && " · showing the latest 500, narrow the dates to see older ones"}
        </span>
      </div>

      <div className="qd-admin-card overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead className="bg-gray-50 text-xs uppercase tracking-widest text-gray-500">
            <tr>
              <th className="text-left p-3">Order</th>
              <th className="text-left p-3">Table</th>
              <th className="text-left p-3">Placed</th>
              <th className="text-left p-3" title="Waiting before kitchen started">Received</th>
              <th className="text-left p-3" title="Cooking time">Preparing</th>
              <th className="text-left p-3" title="Ready, waiting to be served">Ready</th>
              <th className="text-left p-3" title="Served, waiting for payment">Served→Paid</th>
              <th className="text-left p-3">Total time</th>
              <th className="text-left p-3">Amount</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((o) => <HistoryRow key={o.id} o={o} />)}
            {shown.length === 0 && <tr><td colSpan={10} className="p-8 text-center text-gray-400">{orders.length ? "No orders match your filters." : "No finished orders in this range"}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function fmtDur(ms) {
  if (ms == null || !isFinite(ms) || ms < 0) return "—";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60), sec = s % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${String(sec).padStart(2, "0")}s`;
  return `${sec}s`;
}

function HistoryRow({ o }) {
  const at = o.status_at || {};
  const p = (k) => (at[k] ? Date.parse(at[k]) : null);
  const received = p("received") ?? Date.parse(o.created_at);
  const prep = p("preparing"), ready = p("ready"), served = p("fulfilled");
  const end = p("completed") || p("cancelled") || Date.parse(o.updated_at);
  const seg = (a, b) => (a != null && b != null ? b - a : null);
  const placed = new Date(o.created_at);
  return (
    <tr data-testid={`hist-row-${o.id}`} className="border-t border-gray-100">
      <td className="p-3 font-mono-alt text-xs">#{o.id.slice(0, 6).toUpperCase()}</td>
      <td className="p-3">{o.table_label}</td>
      <td className="p-3 text-gray-600 whitespace-nowrap">{placed.toLocaleDateString([], { day: "2-digit", month: "short" })} {placed.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}</td>
      <td className="p-3">{fmtDur(seg(received, prep ?? end))}</td>
      <td className="p-3">{fmtDur(seg(prep, ready ?? (prep ? end : null)))}</td>
      <td className="p-3">{fmtDur(seg(ready, served ?? (ready ? end : null)))}</td>
      <td className="p-3">{fmtDur(seg(served, served ? end : null))}</td>
      <td className="p-3 font-semibold">{fmtDur(seg(received, end))}</td>
      <td className="p-3">₹{o.total}</td>
      <td className="p-3">
        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${o.status === "completed" ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}>{o.status}</span>
      </td>
    </tr>
  );
}
